import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AdminAPI } from "@/lib/admin-api";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";

interface Form {
  name: string;
  trigger_type: string;
  reward_type: string;
  formula_type: string;
  applies_to_stage_number: string;
  description: string;
  // formula fields
  amount: string;
  percent: string;
  reference_field: string;
}

interface Tier { min_stage: number; max_stage: number; amount: string; }

export function RewardRuleBuilder({
  open, onClose, onSaved,
}: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<Form>({
    defaultValues: {
      trigger_type: "stage_completed",
      reward_type: "cash",
      formula_type: "fixed",
      reference_field: "entry_amount",
    },
  });
  const formulaType = watch("formula_type");
  const [tiers, setTiers] = useState<Tier[]>([
    { min_stage: 1, max_stage: 3, amount: "5.00" },
    { min_stage: 4, max_stage: 7, amount: "20.00" },
  ]);

  const create = useMutation({
    mutationFn: (d: Form) => {
      const formula_config: Record<string, unknown> =
        d.formula_type === "fixed"      ? { amount: d.amount }
      : d.formula_type === "percentage" ? { percent: d.percent, reference_field: d.reference_field }
      : d.formula_type === "tiered"     ? { tiers }
      : d.formula_type === "campaign"   ? { amount: d.amount, trigger_types: [d.trigger_type] }
      : {};

      return AdminAPI.createRewardRule({
        name: d.name,
        trigger_type: d.trigger_type,
        reward_type: d.reward_type,
        formula_type: d.formula_type,
        formula_config,
        applies_to_stage_number: d.applies_to_stage_number
          ? Number(d.applies_to_stage_number) : null,
        description: d.description,
      });
    },
    onSuccess: () => {
      toast.success("Reward rule created");
      onSaved();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="New Reward Rule" size="xl">
      <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-5">
        <Input label="Rule name" placeholder="Stage 3 Bonus"
               error={errors.name?.message}
               {...register("name", { required: "Required" })} />

        <div className="grid grid-cols-3 gap-4">
          <Select label="Trigger" options={[
            { value: "stage_completed",  label: "Stage Completed"  },
            { value: "entry_completed",  label: "Entry Completed"  },
            { value: "admin_adjustment", label: "Admin Adjustment" },
          ]} {...register("trigger_type", { required: true })} />
          <Select label="Reward type" options={[
            { value: "cash",        label: "Cash"        },
            { value: "bonus",       label: "Bonus"       },
            { value: "promotional", label: "Promotional" },
            { value: "adjustment",  label: "Adjustment"  },
          ]} {...register("reward_type", { required: true })} />
          <Input label="Only for stage # (optional)" type="number"
                 hint="Blank = all stages"
                 {...register("applies_to_stage_number")} />
        </div>

        <div>
          <div className="text-sm font-semibold text-surface-800 mb-2">Formula</div>
          <Select label="Formula type" options={[
            { value: "fixed",      label: "Fixed amount"     },
            { value: "percentage", label: "Percentage"       },
            { value: "tiered",     label: "Tiered by stage"  },
            { value: "campaign",   label: "Campaign-based"   },
          ]} {...register("formula_type", { required: true })} />
        </div>

        <div className="border border-surface-200 rounded-lg p-4 bg-surface-50/50">
          {formulaType === "fixed" && (
            <Input label="Amount ($)" type="number" step="0.01"
                   {...register("amount", { required: "Required" })} />
          )}
          {formulaType === "percentage" && (
            <div className="grid grid-cols-2 gap-4">
              <Input label="Percent (%)" type="number" step="0.01"
                     {...register("percent", { required: "Required" })} />
              <Input label="Reference field"
                     hint="Field name from event payload"
                     {...register("reference_field")} />
            </div>
          )}
          {formulaType === "tiered" && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-surface-800">Tier definitions</div>
              {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-end">
                  <Input label={i === 0 ? "Min stage" : ""} type="number" value={t.min_stage}
                         onChange={(e) => setTiers(prev => prev.map((p, idx) =>
                           idx === i ? { ...p, min_stage: Number(e.target.value) } : p))} />
                  <Input label={i === 0 ? "Max stage" : ""} type="number" value={t.max_stage}
                         onChange={(e) => setTiers(prev => prev.map((p, idx) =>
                           idx === i ? { ...p, max_stage: Number(e.target.value) } : p))} />
                  <Input label={i === 0 ? "Amount" : ""} type="number" step="0.01" value={t.amount}
                         onChange={(e) => setTiers(prev => prev.map((p, idx) =>
                           idx === i ? { ...p, amount: e.target.value } : p))} />
                  <button type="button"
                          onClick={() => setTiers(prev => prev.filter((_, idx) => idx !== i))}
                          className="h-10 w-10 flex items-center justify-center text-surface-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" type="button"
                      leftIcon={<Plus className="h-3.5 w-3.5" />}
                      onClick={() => setTiers(prev => [
                        ...prev,
                        { min_stage: 1, max_stage: 3, amount: "0.00" },
                      ])}>
                Add tier
              </Button>
            </div>
          )}
          {formulaType === "campaign" && (
            <Input label="Campaign amount ($)" type="number" step="0.01"
                   {...register("amount")} />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-800 mb-1.5">Description</label>
          <textarea
            {...register("description")}
            className="w-full h-20 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 focus:outline-none"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={create.isPending}>Create rule</Button>
        </div>
      </form>
    </Modal>
  );
}
